import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Play, ArrowRight, Bookmark, TrendingUp, Sparkles, Edit3, Trash2, Download, ShoppingBag, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { MyGiving } from "../components/MyGiving";
import { MyMembership } from "../components/MyMembership";
import { MyHeartRequests } from "../components/MyHeartRequests";
import { MerchOrderCard } from "./MerchThankYou";

function MerchOrdersSection() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/me/merch-orders").then((r) => setOrders(r.data || [])).catch(() => setOrders([])); }, []);
  if (!orders || orders.length === 0) return null;
  return (
    <section data-testid="dashboard-merch-orders">
      <div className="eyebrow mb-1">Merchandise Orders</div>
      <h2 className="font-serif text-2xl mb-4" style={{ color: "var(--burgundy)" }}>Your Temple Store orders.</h2>
      <div className="space-y-3">{orders.map((o) => <MerchOrderCard key={o.id} o={o} />)}</div>
    </section>
  );
}

const STAGES = [
  { step: 1, name: "ENTER", verb: "Cross the threshold of stillness." },
  { step: 2, name: "REMEMBER", verb: "Recall the spark within the heart." },
  { step: 3, name: "EXPLORE", verb: "Examine wisdom with a sacred question." },
  { step: 4, name: "DISCERN", verb: "Separate ego noise from soulful truth." },
  { step: 5, name: "INTEGRATE", verb: "Weave realization into daily life." },
  { step: 6, name: "EMBODY", verb: "Live compassion as physical presence." },
  { step: 7, name: "SYNTHESIZE", verb: "Unify heart with community." },
  { step: 8, name: "RETURN", verb: "Rest in the unconditioned Divine." },
];

export default function MyTemple() {
  const { user, authLoading, saved, rsvps, recentlyPlayed, enrollments, reflections, playTrack, updateReflection, deleteReflection, purchases, refreshPurchases } = useApp();
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [ackOpen, setAckOpen] = useState(false);
  const [reflectionSearch, setReflectionSearch] = useState("");

  useEffect(() => {
    Promise.all([api.get("/content/courses"), api.get("/content/events")])
      .then(([c, e]) => { setCourses(c.data || []); setEvents(e.data || []); })
      .catch(() => {});
    if (user && !user.journal_safety_ack_at) setAckOpen(true);
    // Always re-hydrate purchases when landing on My Temple so a purchase
    // completed elsewhere in the tab (e.g. after Stripe redirect) is visible.
    if (user) refreshPurchases();
  }, [user, refreshPurchases]);

  const ackJournal = async () => {
    try {
      await api.post("/me/journal-acknowledge", { acknowledged: true });
      setAckOpen(false);
    } catch {}
  };

  if (authLoading) return <div className="max-w-7xl mx-auto px-4 py-24 text-center" style={{ color: "var(--text-muted)" }}>Opening the chamber…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const enrolledPublished = enrollments.map((en) => ({ en, course: courses.find((c) => c.id === en.course_id) })).filter((x) => x.course);
  const latestEnrollment = enrolledPublished.length > 0 ? enrolledPublished[0].en : null;
  const activeCourse = enrolledPublished.length > 0 ? enrolledPublished[0].course : null;
  const hasUnavailableEnrollment = !activeCourse && courses.length > 0 && enrollments.length > 0;
  const activeProgress = latestEnrollment ? latestEnrollment.progress : 0;
  const currentStageStep = user.current_stage || 1;
  const stage = STAGES.find((s) => s.step === currentStageStep) || STAGES[0];
  const savedItems = saved.length > 0 ? saved.slice(0, 4).map((s) => ({ ...(s.snapshot || {}), item_type: s.item_type })) : [];
  const recent = recentlyPlayed.length > 0 ? recentlyPlayed : [];
  const avatarUrl = user.avatar_url || "https://images.unsplash.com/photo-1527203561188-dae1bc1a417f?crop=entropy&cs=srgb&fm=jpg&w=200&q=80";

  return (
    <div>
      <section className="relative overflow-hidden" style={{ background: "var(--surface-deep)" }}>
        <div className="grain absolute inset-0" />
        <div className="bloom absolute inset-0 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 relative" style={{ color: "var(--bg-cream)" }}>
          <div className="eyebrow mb-2" style={{ color: "var(--gold-soft)" }}>My Temple</div>
          <div className="flex items-center gap-4">
            <img src={avatarUrl} alt="" className="w-14 h-14 rounded-full object-cover border-2" style={{ borderColor: "var(--gold)" }} />
            <div>
              <h1 className="font-serif text-3xl sm:text-4xl leading-tight">Welcome home, {user.name.split(" ")[0]}.</h1>
              <p className="text-sm opacity-85 mt-1">The chamber is quiet. Sit as long as you need.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section className="rounded-2xl border overflow-hidden lift" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="dashboard-continue-learning">
            {activeCourse ? (
              <div className="grid md:grid-cols-2">
                <div className="p-6 flex flex-col">
                  <div className="eyebrow mb-2">Continue Learning</div>
                  <div className="font-mono text-[10px] tracking-widest uppercase" style={{ color: "var(--gold-dark)" }}>Stage {String(activeCourse.stage).padStart(2, "0")} · {STAGES[activeCourse.stage - 1]?.name}</div>
                  <h3 className="font-serif text-2xl mt-1 leading-tight" style={{ color: "var(--burgundy)" }}>{activeCourse.title}</h3>
                  <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>Encounter {(latestEnrollment.current_lesson_index || 0) + 1} of {activeCourse.lesson_count}</p>
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] font-mono mb-1" style={{ color: "var(--gold-dark)" }}>
                      <span>PROGRESS</span><span>{activeProgress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ background: "var(--border-cream)" }}>
                      <div className="h-full rounded-full" style={{ width: `${activeProgress}%`, background: "var(--burgundy)" }} />
                    </div>
                  </div>
                  <Link to={`/learn/${activeCourse.id}`} data-testid="continue-learning-btn"
                    className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium ink-link self-start" style={{ color: "var(--burgundy)" }}>
                    Continue where you left off <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                {activeCourse.art_url && <div className="relative h-52 md:h-full min-h-[220px]"><img src={assetUrl(activeCourse.art_url)} alt="" className="w-full h-full object-cover" /></div>}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="eyebrow mb-2">Continue Learning</div>
                <p className="font-serif text-lg mb-4" style={{ color: "var(--burgundy)" }} data-testid="continue-learning-empty-text">{hasUnavailableEnrollment ? "Your course is resting for now — it is not currently open." : "You haven't begun a course yet."}</p>
                <Link to="/learn" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Explore the Learning Circle</Link>
              </div>
            )}
          </section>

          <section data-testid="dashboard-saved-teachings">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="eyebrow mb-1">Saved Teachings</div>
                <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>Return to what stirred you.</h2>
              </div>
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>{saved.length} saved</span>
            </div>
            {savedItems.length === 0 ? (
              <div className="rounded-xl border p-6 text-sm text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }}>
                You haven't saved anything yet. Tap the bookmark on any teaching to return to it here.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {savedItems.map((s, i) => (
                  <div key={(s.id || "") + i} className="rounded-xl border p-3 flex gap-3 lift" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid={`saved-item-${i}`}>
                    {s.art && <img src={s.art} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="eyebrow text-[9px]">{s.kind || s.type || s.item_type || "Teaching"}</div>
                      <div className="font-serif text-sm leading-tight truncate" style={{ color: "var(--burgundy)" }}>{s.title}</div>
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{s.host || s.author}</div>
                    </div>
                    <Bookmark className="w-4 h-4 flex-shrink-0 fill-current" style={{ color: "var(--gold-dark)" }} />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section data-testid="dashboard-recently-played">
            <div className="eyebrow mb-1">Recently Played</div>
            <h2 className="font-serif text-2xl mb-4" style={{ color: "var(--burgundy)" }}>Your listening path.</h2>
            {recent.length === 0 ? (
              <div className="rounded-xl border p-6 text-sm text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }}>
                Nothing here yet — press play on any teaching or radio show and it will appear here.
              </div>
            ) : (
              <ul className="rounded-2xl border divide-y overflow-hidden" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
                {recent.map((t, i) => (
                  <li key={(t.id || "") + i} className="flex items-center gap-4 p-3 hover:bg-[color:var(--burgundy-tint)]" data-testid={`recent-item-${i}`}>
                    {t.art && <img src={t.art} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-base truncate" style={{ color: "var(--burgundy)" }}>{t.title}</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t.host} · {t.duration}</div>
                    </div>
                    <button data-testid={`recent-play-${i}-btn`} onClick={() => playTrack(t)}
                      className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ background: "var(--burgundy)", color: "var(--gold)" }} aria-label="Play">
                      <Play className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Store Purchases */}
          <section data-testid="dashboard-store-purchases">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="eyebrow mb-1">Store Purchases</div>
                <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>Your digital offerings.</h2>
              </div>
              <button
                data-testid="refresh-purchases-btn"
                onClick={refreshPurchases}
                className="text-xs ink-link"
                style={{ color: "var(--burgundy)" }}
              >
                Refresh
              </button>
            </div>
            {(!purchases || purchases.length === 0) ? (
              <div className="rounded-xl border p-6 text-sm text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }}>
                No purchases yet. <Link to="/store" className="ink-link font-medium" style={{ color: "var(--burgundy)" }}>Visit the Temple Store</Link>.
              </div>
            ) : (
              <ul className="rounded-2xl border divide-y overflow-hidden" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
                {purchases.map((p) => {
                  const backend = process.env.REACT_APP_BACKEND_URL;
                  const dlHref = `${backend}/api/me/purchases/download?token=${encodeURIComponent(p.download_token || "")}`;
                  return (
                    <li key={p.session_id} className="flex items-center gap-4 p-4" data-testid={`purchase-row-${p.session_id}`}>
                      <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: "var(--burgundy-tint)", color: "var(--burgundy)" }}>
                        <ShoppingBag className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-base truncate" style={{ color: "var(--burgundy)" }}>{p.product_name}</div>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                          {(p.updated_at || "").slice(0, 10)} · {new Intl.NumberFormat("en-US", { style: "currency", currency: (p.currency || "usd").toUpperCase() }).format((p.amount_cents || 0) / 100)}
                        </div>
                      </div>
                      <a
                        href={dlHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`purchase-download-${p.session_id}-btn`}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium"
                        style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <MerchOrdersSection />

          <MyMembership />

          <MyGiving />

          <MyHeartRequests />

          {/* My Reflections — private journal */}
          <section data-testid="dashboard-reflections">
            <div className="eyebrow mb-1">My Reflections</div>
            <h2 className="font-serif text-2xl mb-2" style={{ color: "var(--burgundy)" }}>A private record of what has been stirring within you.</h2>
            <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>These belong to you. Only you can read them.</p>
            {user.journal_safety_ack_at && (
              <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest mb-3" style={{ background: "var(--teal-tint)", color: "var(--teal-deep)" }} data-testid="journal-ack-indicator">
                <ShieldCheck className="w-3 h-3" /> Journal privacy notice acknowledged · {user.journal_safety_ack_at.slice(0, 10)}
              </div>
            )}
            <div className="rounded-lg border p-3 mb-4 text-[11px] leading-relaxed" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold-soft)", color: "var(--text-muted)" }}>
              Your journal is private within the Temple and is not routinely read by Temple staff. Entries may be automatically screened for serious safety concerns. If an entry indicates possible imminent risk of harm, limited authorized personnel may review it. The Journal is not an emergency service, confessional, psychotherapy service, or substitute for professional care. Automated screening does not detect every concern.
            </div>
            {reflections.length > 0 && (
              <input data-testid="reflections-search-input" value={reflectionSearch}
                onChange={(e) => setReflectionSearch(e.target.value)}
                placeholder="Search your reflections…"
                className="w-full rounded-lg border px-3 py-2 text-sm bg-transparent mb-3"
                style={{ borderColor: "var(--border-cream)" }} />
            )}
            {reflections.length === 0 ? (
              <div className="rounded-xl border p-6 text-sm text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }}>
                No reflections yet. Write one in any Encounter and it will appear here.
              </div>
            ) : (
              <div className="space-y-3">
                {reflections
                  .filter((r) => !reflectionSearch || (r.text || "").toLowerCase().includes(reflectionSearch.toLowerCase()) || (r.course_title || "").toLowerCase().includes(reflectionSearch.toLowerCase()))
                  .map((r) => (<ReflectionCard key={r.id} r={r} onUpdate={updateReflection} onDelete={deleteReflection} />))}
              </div>
            )}
          </section>
        </div>

        <div className="space-y-8">
          <MyGatherings />

          <section className="rounded-2xl border p-6 relative overflow-hidden" style={{ background: "var(--obsidian)", color: "var(--bg-cream)", borderColor: "var(--dark-card)" }} data-testid="dashboard-my-journey">
            <div className="grain absolute inset-0" />
            <div className="relative">
              <div className="eyebrow mb-2" style={{ color: "var(--gold-soft)" }}>My Journey</div>
              <div className="font-serif text-2xl mb-1" style={{ color: "var(--gold)" }}>Stage {String(currentStageStep).padStart(2, "0")} · {stage.name}</div>
              <p className="text-sm opacity-85 mb-6">You are in the season of <em>{stage.name.toLowerCase()}</em>.</p>
              <div className="space-y-3">
                {STAGES.map((s) => {
                  const done = s.step < currentStageStep;
                  const current = s.step === currentStageStep;
                  return (
                    <div key={s.step} className="flex items-center gap-3" data-testid={`journey-stage-${s.name.toLowerCase()}`}>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center font-mono text-[10px] flex-shrink-0"
                        style={{ background: done || current ? "var(--gold)" : "transparent", color: done || current ? "var(--burgundy)" : "var(--bg-cream)",
                          border: `1px solid ${done || current ? "var(--gold)" : "var(--gold-soft)"}`, opacity: done || current ? 1 : 0.6 }}>
                        {done ? "✓" : String(s.step).padStart(2, "0")}
                      </div>
                      <div className="text-sm flex-1" style={{ opacity: done ? 0.6 : current ? 1 : 0.5 }}>
                        <span className="font-serif">{s.name}</span>
                        {current && <span className="ml-2 text-[10px] font-mono" style={{ color: "var(--gold-soft)" }}>· NOW</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="hairline my-6" />
              <div className="flex items-center gap-2 text-xs opacity-85">
                <TrendingUp className="w-4 h-4" /> {enrolledPublished.length} course{enrolledPublished.length === 1 ? "" : "s"} in progress · {reflections.length} reflection{reflections.length === 1 ? "" : "s"} written
              </div>
            </div>
          </section>

          <div className="rounded-2xl border p-6 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold-soft)" }}>
            <Sparkles className="w-5 h-5 mx-auto mb-2" style={{ color: "var(--gold-dark)" }} />
            <div className="eyebrow mb-2">Whisper of the day</div>
            <p className="font-serif italic text-base" style={{ color: "var(--burgundy)" }}>
              "You do not fail the path when your mind wanders. You walk the path each time you return."
            </p>
          </div>
        </div>
      </div>

      {ackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(48,43,41,0.72)" }}>
          <div data-testid="journal-ack-modal" className="w-full max-w-lg rounded-2xl p-6 sm:p-8" style={{ background: "var(--bg-cream)" }}>
            <div className="eyebrow mb-2">Before you begin journaling</div>
            <h3 className="font-serif text-2xl mb-4" style={{ color: "var(--burgundy)" }}>A note about your Journal.</h3>
            <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--text-obsidian)" }}>
              Your journal is private within the Temple and is not routinely read by Temple staff. However, journal entries may be automatically screened for serious safety concerns. If an entry indicates a possible imminent risk of harm to you or another person, limited authorized personnel may review the flagged content and may take steps intended to protect life or safety.
            </p>
            <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--text-obsidian)" }}>
              The Journal is not an emergency service, confessional, psychotherapy service, or substitute for professional care. Automated screening is not guaranteed to detect every safety concern, and journal entries are not continuously monitored in real time.
            </p>
            <button data-testid="journal-ack-btn" onClick={ackJournal}
              className="w-full rounded-full py-3 text-sm font-medium"
              style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
              I understand — enter my journal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReflectionCard({ r, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.text);

  const save = async () => {
    try { await onUpdate(r.id, text); toast("Reflection updated"); setEditing(false); }
    catch { toast.error("Could not save"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this reflection? This cannot be undone.")) return;
    try { await onDelete(r.id); toast("Reflection deleted"); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="rounded-2xl border p-5" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid={`reflection-card-${r.id}`}>
      <div className="flex items-start justify-between mb-2 gap-3">
        <div className="min-w-0">
          <div className="eyebrow text-[10px]">{[r.course_title, r.lesson_title].filter(Boolean).join(" · ") || "Reflection"}</div>
          <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{(r.created_at || "").slice(0, 10)}</div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid={`reflection-edit-${r.id}-btn`} onClick={() => setEditing((v) => !v)} className="p-1.5 rounded-full hover:bg-[color:var(--burgundy-tint)]"><Edit3 className="w-3.5 h-3.5" /></button>
          <button data-testid={`reflection-delete-${r.id}-btn`} onClick={remove} className="p-1.5 rounded-full hover:bg-[color:var(--burgundy-tint)]"><Trash2 className="w-3.5 h-3.5" style={{ color: "var(--rose-dark)" }} /></button>
        </div>
      </div>
      {r.prompt && <div className="font-serif italic text-sm mb-2" style={{ color: "var(--text-muted)" }}>"{r.prompt}"</div>}
      {editing ? (
        <>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)}
            data-testid={`reflection-edit-textarea-${r.id}`}
            className="w-full rounded-lg p-3 text-sm border resize-none focus:outline-none"
            style={{ background: "var(--bg-cream)", borderColor: "var(--border-cream)" }} />
          <div className="flex items-center gap-2 mt-2">
            <button data-testid={`reflection-save-${r.id}-btn`} onClick={save} className="rounded-full px-4 py-1.5 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save</button>
            <button onClick={() => { setEditing(false); setText(r.text); }} className="text-xs ink-link" style={{ color: "var(--text-muted)" }}>Cancel</button>
          </div>
        </>
      ) : (
        <p className="text-sm whitespace-pre-line" style={{ color: "var(--text-obsidian)" }}>{r.text}</p>
      )}
    </div>
  );
}

function MyGatherings() {
  const { refreshRsvps } = useApp();
  const [regs, setRegs] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const load = async () => { try { const { data } = await api.get("/me/registrations"); setRegs(data || []); } catch {} };
  useEffect(() => { load(); }, []);
  const cancel = async (r) => {
    if (!window.confirm(`Cancel your registration for "${r.event?.title || r.snapshot?.title}"?`)) return;
    try { await api.post(`/me/registrations/${r.id}/cancel`); toast("Registration cancelled"); load(); refreshRsvps?.(); } catch { toast.error("Could not cancel"); }
  };
  const upcoming = regs.filter((r) => !r.past && r.status !== "cancelled");
  const past = regs.filter((r) => r.past || r.status === "cancelled");
  const Row = ({ r }) => {
    const e = r.event || r.snapshot || {};
    const parts = (e.date || "").split(" ");
    const time = e.time || [e.start_time, e.end_time].filter(Boolean).join(" – ");
    return (
      <li className="flex gap-3" data-testid={`my-gathering-${r.id}`}>
        <div className="w-12 rounded-md text-center py-2 flex-shrink-0" style={{ background: r.status === "confirmed" ? "var(--burgundy)" : "var(--teal)", color: "var(--bg-cream)" }}>
          <div className="text-[10px] font-mono uppercase">{parts[0] || ""}</div>
          <div className="font-serif text-lg leading-none">{(parts[1] || "").replace(",", "")}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-base leading-tight" style={{ color: "var(--burgundy)" }}>{e.title}</div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>{time}{e.time_zone ? ` ${e.time_zone}` : ""} · {e.format}{e.location ? ` · ${e.location}` : ""}</div>
          <div className="text-[11px] mt-1 flex flex-wrap items-center gap-3">
            <span className="font-mono uppercase tracking-widest" data-testid={`my-gathering-status-${r.id}`} style={{ color: r.status === "confirmed" ? "var(--gold-dark)" : r.status === "waitlisted" ? "var(--teal)" : "var(--text-muted)" }}>{r.status}{r.status === "waitlisted" && r.waitlist_position ? ` · #${r.waitlist_position}` : ""}</span>
            {e.virtual_url && r.status === "confirmed" && !r.past && <a href={e.virtual_url} target="_blank" rel="noreferrer" className="ink-link" style={{ color: "var(--teal-deep)" }} data-testid={`my-gathering-virtual-${r.id}`}>Join virtual room →</a>}
            {r.status !== "cancelled" && !r.past && <button onClick={() => cancel(r)} className="ink-link" style={{ color: "var(--rose-dark)" }} data-testid={`my-gathering-cancel-${r.id}`}>Cancel</button>}
          </div>
        </div>
      </li>
    );
  };
  return (
    <section className="rounded-2xl border p-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="dashboard-upcoming-gatherings">
      <div className="eyebrow mb-3">My Gatherings</div>
      {upcoming.length === 0 ? <div className="text-sm" style={{ color: "var(--text-muted)" }}>You are not registered for any upcoming gatherings.</div>
        : <ul className="space-y-4">{upcoming.map((r) => <Row key={r.id} r={r} />)}</ul>}
      {past.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowPast(!showPast)} className="text-xs ink-link" style={{ color: "var(--text-muted)" }} data-testid="my-gatherings-history-toggle">{showPast ? "Hide" : "Show"} history ({past.length})</button>
          {showPast && <ul className="space-y-3 mt-3 opacity-80">{past.map((r) => <Row key={r.id} r={r} />)}</ul>}
        </div>
      )}
      <Link to="/gather" data-testid="dashboard-view-gatherings-link" className="mt-5 inline-flex items-center gap-1 text-xs font-medium ink-link" style={{ color: "var(--burgundy)" }}>
        All gatherings <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </section>
  );
}
